import { colors, system } from "@/theme";
import type { BackendResource } from "@imminently/immi-query";
import type { Task, TaskPriority, TaskStatus } from "@packages/commons";
import type { TEditableProps } from "@udecode/plate-core";

export type SelectOption<T = any> = {
  label: string;
  value: T;
};

export const statusOptions: SelectOption<TaskStatus>[] = [
  { label: "Not Started", value: "not-started" },
  { label: "In Progress", value: "in-progress" },
  { label: "Review", value: "review" },
  { label: "Approved", value: "approved" },
  { label: "Closed", value: "closed" },
  { label: "Rejected", value: "rejected" },
  { label: "Blocked", value: "blocked" },
];

export const priorityOptions: SelectOption<TaskPriority>[] = [
  { label: "Low", value: "low" },
  { label: "Medium", value: "medium" },
  { label: "High", value: "high" },
  { label: "Urgent", value: "urgent" },
];

export const sortOptions: SelectOption[] = [
  {
    label: "Due Date",
    value: "dueDate",
  },
  {
    label: "Priority",
    value: "priority",
  },
  {
    label: "Status",
    value: "status",
  },
];

/** Maps priority to a system color */
export const priorityColor = {
  low: colors.ultimateGrey,
  medium: system.purpleSlate,
  high: system.trafficAmber85,
  urgent: system.trafficRed85,
};

/** Maps priority to an ordered number */
export const priorityNumber = {
  low: 1,
  medium: 2,
  high: 3,
  urgent: 4,
};

/** Maps task status to a system color */
export const statusColor = {
  notstarted: "#6B7280", // Slate gray
  inprogress: "#2563EB", // Bright blue
  "not-started": "#6B7280", // Slate gray
  "in-progress": "#2563EB", // Bright blue
  review: "#9333EA", // Vibrant purple
  approved: "#059669", // Emerald green
  complete: "#059669", // Emerald green
  closed: "#1F2937", // Dark gray
  rejected: "#DC2626", // Red
  blocked: "#D97706", // Amber
};

/** Maps task status to appropriate text color for contrast */
export const statusTextColor = {
  notstarted: "#FFFFFF",
  inprogress: "#FFFFFF",
  "not-started": "#FFFFFF",
  "in-progress": "#FFFFFF",
  review: "#FFFFFF",
  approved: "#FFFFFF",
  complete: "#FFFFFF",
  closed: "#FFFFFF",
  rejected: "#FFFFFF",
  blocked: "#FFFFFF",
};

/** Maps task status to an ordered number */
export const statusNumber = {
  notstarted: 1,
  inprogress: 2,
  review: 3,
  approved: 4,
  closed: 5,
  rejected: 6,
  blocked: 7,
};

export type FormTask = Partial<Omit<Task, "status" | "priority" | "dueDate">> & {
  status: TaskStatus;
  priority: TaskPriority;
  dueDate?: string;
};

export type FlowFormTask = Partial<Omit<Task, "status" | "priority" | "dueDate">> & {
  status: TaskStatus;
  priority: TaskPriority;
  dueDate?: string;
  justification: string;
  interviewData?: any[]; // interview data
};

export const newParagraph = (text: string) => ({ type: "paragraph", children: [{ text }] });

export const createNewTask = (type: string): FormTask => ({
  type,
  title: "",
  description: [newParagraph("")],
  status: statusOptions[0].value,
  priority: priorityOptions[0].value,
  tags: [],
  assignees: [],
  assigneesData: [],
  dueDate: new Date(Date.now()).toISOString().slice(0, 10),
  comments: [],
});

export const createFormTask = (task: Task, type?: string): FormTask => ({
  ...createNewTask(type ?? task.type), // add defaults
  ...task, // add overrides we have
  // convert to FE values
  description: task.description
    ? typeof task.description === "string"
      ? [newParagraph(task.description)]
      : task.description
    : [newParagraph("")],
  status: statusOptions.find((option) => option.value === task.status)?.value ?? statusOptions[0].value,
  priority: priorityOptions.find((option) => option.value === task.priority)?.value ?? priorityOptions[0].value,
  dueDate: task.dueDate ? task.dueDate.slice(0, 10) : undefined,
});

export const createFlowTask = (task: Task): FlowFormTask => ({
  // @ts-ignore
  assignees: [],
  // @ts-ignore
  assigneesData: [],
  ...task, // add overrides we haveassignees: [],
  status: statusOptions.find((option) => option.value === task.status)?.value ?? statusOptions[0].value,
  priority: priorityOptions.find((option) => option.value === task.priority)?.value ?? priorityOptions[0].value,
  dueDate: task.dueDate ? task.dueDate.slice(0, 10) : undefined,
  justification: "",
});

export const editableProps = (props?: TEditableProps) => ({
  autoFocus: false,
  spellCheck: false,
  ...props,
});

export const validateTask = (task: BackendResource<Task>): Task => {
  // TODO what do i even add to validate and what do i do if it fails?
  return task as Task;
};
